import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_MENU_ARGS,
  INPUT_SELECT_ALL_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { HIDDEN_SELECTOR } from '../../../../utils/keyboard-nav-support';
import { ensureSafeComponent } from '@embroider/util';
import { eq, or } from 'ember-truth-helpers';
import { fn, hash } from '@ember/helper';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import CheckboxGroup from '../group/checkbox.gjs';
import Menu from './menu/base.gjs';
import NoResults from '../../core/menu/no-results.gjs';
import Super from '../../core/base.gjs';
import boldMatchingText from '../../../../modifiers/bold-matching-text';

const SELECT_ALL_LABEL_DEFAULT = 'Select all';
const SELECT_ALL_THRESHOLD = 6;
const Item = <template>
  <@checkbox
    @checked={{@checked}}
    @inputClasses={{@inputClasses}}
    @value={{@value}}
    {{boldMatchingText @searchValue}}
    ...attributes
  >
    {{yield}}
  </@checkbox>
</template>;

const METADATA = Metadata.define({
  description: 'Multi-select menu for search input with checkbox options and select-all support',
  extends: [
    pick(MENU_ARGS, ['anchorElement']),
    pick(INPUT_MENU_ARGS, [{ name: 'displayedItemCount', required: true }, 'noResultsText']),
    pick(ACTION_ARGS, [{ name: 'onClick', required: true }]),
    pick(COMPONENT_STATE_ARGS, [{ name: 'open', required: true }]),
    INPUT_SELECT_ALL_ARGS,
  ],
  meta: {
    selectAll: Arg.action({
      description: 'Action triggered when the select-all option is toggled',
    }).required(),
  },
  name: 'Search Multiselect Menu',
});

export default class SpdsInputSearchMultiselectMenu extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  additionalClasses = 'spds-input-search-menu-item';
  baseClass = 'spds-input-search-multiselect-menu';

  @tracked checkboxGroup;

  @reads('checkboxGroup.navigableOptions', []) navigableOptions;
  @reads('args.selectAllMin', SELECT_ALL_THRESHOLD) selectAllThreshold;

  get checkboxGroupId() {
    return this.composeSubcomponentId('multi-menu');
  }

  get showSelectAll() {
    return this.args.displayedItemCount >= this.selectAllThreshold && this.selectAllThreshold >= 0;
  }

  <template>
    <Menu
      @anchorElement={{@anchorElement}}
      @id={{this.id}}
      @open={{@open}}
      @multiselect={{true}}
      @noItemsDisplayed={{eq @displayedItemCount 0}}
      @noResultsText={{@noResultsText}}
      class={{this.classes}}
      data-test-spds-input-search-multiselect-menu
      {{this.validateArguments}}
      ...attributes
    >
      <CheckboxGroup
        @groupName={{this.checkboxGroupId}}
        @hideDescription={{true}}
        @id={{this.checkboxGroupId}}
        @layout="column"
        @onClick={{@onClick}}
        @onSetup={{@onClick}}
        @registerComponent={{fn this.registerComponent "checkboxGroup"}}
        @selectAll={{@selectAll}}
        @selected={{@selected}}
        @unregisterComponent={{fn this.unregisterComponent "checkboxGroup"}}
        as |cg|
      >
        <cg.selectAll
          @inputClasses="{{this.additionalClasses}} select-all"
          @label={{or @selectAllLabel SELECT_ALL_LABEL_DEFAULT}}
          class={{unless this.showSelectAll HIDDEN_SELECTOR}}
          data-test-spds-input-search-multiselect-menu-select-all-option
        />
        {{yield
          (hash
            item=(component
              Item
              checkbox=(ensureSafeComponent cg.checkbox)
              inputClasses=this.additionalClasses
              searchValue=@searchValue
            )
            noResults=(component NoResults)
          )
        }}
      </CheckboxGroup>
    </Menu>
  </template>
}

export const SEARCH_MULTISELECT_MENU_METADATA = SpdsInputSearchMultiselectMenu.metadata;
